import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Accordion, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import {
  createMaintenanceWindowAPI,
  deleteMaintenanceWindowAPI,
  getGeneralSettingsAPI,
  updateGeneralSettingsAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './GeneralSettings.module.scss';

const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
];
const TIMEZONES = [
  { value: 'Asia/Muscat', label: 'Asia/Muscat' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'Europe/London' },
];

export default function GeneralSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [mwOpen, setMwOpen] = useState(false);
  const [mwStart, setMwStart] = useState(new Date());
  const [mwHours, setMwHours] = useState(2);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      platformName: '',
      baseUrl: '',
      supportEmail: '',
      defaultLocale: LOCALES[0],
      defaultTimezone: TIMEZONES[0],
      dateFormat: 'YYYY-MM-DD',
      timeFormat: '24h',
    },
  });
  const [windows, setWindows] = useState([]);

  const baseUrlVal = watch('baseUrl');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getGeneralSettingsAPI();
      const g = res.data;
      reset({
        platformName: g.platformName,
        baseUrl: g.baseUrl,
        supportEmail: g.supportEmail,
        defaultLocale: typeof g.defaultLocale === 'object' ? g.defaultLocale : LOCALES.find((l) => l.value === g.defaultLocale) || LOCALES[0],
        defaultTimezone:
          typeof g.defaultTimezone === 'object' ? g.defaultTimezone : TIMEZONES.find((t) => t.value === g.defaultTimezone) || TIMEZONES[0],
        dateFormat: g.dateFormat,
        timeFormat: g.timeFormat,
      });
      setWindows(g.maintenanceWindows || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = handleSubmit(async (values) => {
    try {
      await updateGeneralSettingsAPI({
        ...values,
        defaultLocale: values.defaultLocale?.value || values.defaultLocale,
        defaultTimezone: values.defaultTimezone?.value || values.defaultTimezone,
      });
      toast.success('Saved');
      setSaveOpen(false);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  });

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="General settings" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="General settings" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings' }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="General settings"
        breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Settings' }]}
        secondaryActions={[
          { label: 'Feature flags', onClick: () => navigate(platformUrl('settings/flags')) },
          { label: 'Storage & retention', onClick: () => navigate(platformUrl('settings/retention')) },
        ]}
      />

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          setSaveOpen(true);
        }}
      >
        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Platform identity</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>Platform name</Form.Label>
                <Controller name="platformName" control={control} render={({ field }) => <Form.Control {...field} />} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="d-flex align-items-center gap-2">
                  Base URL
                  <OverlayTrigger overlay={<Tooltip>Changing base URL affects deep links and webhooks.</Tooltip>}>
                    <span className="text-warning" style={{ cursor: 'help' }}>
                      ⚠
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Controller name="baseUrl" control={control} render={({ field }) => <Form.Control {...field} />} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Support email</Form.Label>
                <Controller name="supportEmail" control={control} render={({ field }) => <Form.Control {...field} type="email" />} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Default locale</Form.Label>
                <Controller name="defaultLocale" control={control} render={({ field }) => <Select options={LOCALES} value={field.value} onChange={field.onChange} />} />
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>Defaults</Accordion.Header>
            <Accordion.Body>
              <Form.Group className="mb-2">
                <Form.Label>Default timezone</Form.Label>
                <Controller name="defaultTimezone" control={control} render={({ field }) => <Select options={TIMEZONES} value={field.value} onChange={field.onChange} />} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Date format</Form.Label>
                <Controller name="dateFormat" control={control} render={({ field }) => <Form.Control {...field} />} />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Time format</Form.Label>
                <Controller name="timeFormat" control={control} render={({ field }) => (
                  <Form.Select {...field}>
                    <option value="24h">24h</option>
                    <option value="12h">12h</option>
                  </Form.Select>
                )} />
              </Form.Group>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>Maintenance windows</Accordion.Header>
            <Accordion.Body>
              <Button size="sm" className="mb-2" onClick={() => setMwOpen(true)}>
                Create maintenance window
              </Button>
              <ul className="list-unstyled">
                {windows.map((w) => (
                  <li key={w.id} className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center">
                    <span>
                      {new Date(w.startAt).toLocaleString()} — {w.durationHours}h · {w.addedBy}
                    </span>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={async () => {
                        try {
                          await deleteMaintenanceWindowAPI(w.id);
                          toast.success('Removed');
                          load();
                        } catch (e) {
                          toast.error(e.message);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className={styles.stickySave}>
          <Button type="submit">Save</Button>
        </div>
      </Form>

      <ConfirmModal
        isOpen={saveOpen}
        onClose={() => setSaveOpen(false)}
        onConfirm={() => onSave()}
        title="Save settings?"
        message={baseUrlVal ? `Confirm changes including base URL: ${baseUrlVal}` : 'Save all changes?'}
        confirmLabel="Save"
      />

      <Modal show={mwOpen} onHide={() => setMwOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Maintenance window</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Start</Form.Label>
          <DatePicker selected={mwStart} onChange={(d) => d && setMwStart(d)} showTimeSelect className="form-control mb-2" />
          <Form.Label>Duration (hours)</Form.Label>
          <Form.Control type="number" value={mwHours} onChange={(e) => setMwHours(+e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await createMaintenanceWindowAPI({
                  startAt: mwStart.toISOString(),
                  durationHours: mwHours,
                });
                toast.success('Added');
                setMwOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
